import { AxiosError } from 'axios';
import type { ApiError } from './types';

/**
 * API 에러를 표준화하는 함수
 */
export function handleApiError(error: unknown): ApiError {
  if (error instanceof AxiosError) {
    // API 서버에서 반환한 에러 정보 추출
    const status = error.response?.status || 500;
    const message = extractErrorMessage(error);
    const code = error.response?.data?.code || 'UNKNOWN_ERROR';
    const details = error.response?.data;

    return {
      status,
      message,
      code,
      details,
      originalError: error,
    };
  }

  // Axios 에러가 아닌 일반 에러 처리
  return {
    status: 500,
    message:
      error instanceof Error
        ? error.message
        : '알 수 없는 오류가 발생했습니다.',
    code: 'UNKNOWN_ERROR',
    originalError: error,
  };
}

/**
 * 에러 응답에서 사용자 친화적인 메시지 추출
 */
function extractErrorMessage(error: AxiosError): string {
  // 서버 응답에서 메시지 추출 시도
  if (error.response?.data) {
    const data = error.response.data as any;

    // 직접적인 메시지 필드 확인
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;

    // 중첩된 에러 객체 확인
    if (data.error?.message) return data.error.message;
  }

  // HTTP 상태 코드 기반 기본 메시지
  if (error.response?.status) {
    switch (error.response.status) {
      case 400:
        return '잘못된 요청입니다.';
      case 401:
        return '인증이 필요합니다.';
      case 403:
        return '접근 권한이 없습니다.';
      case 404:
        return '요청한 리소스를 찾을 수 없습니다.';
      case 408:
        return '요청 시간이 초과되었습니다.';
      case 409:
        return '요청이 현재 상태와 충돌합니다.';
      case 422:
        return '입력값이 유효하지 않습니다.';
      case 500:
        return '서버 오류가 발생했습니다.';
      case 502:
        return '게이트웨이 오류가 발생했습니다.';
      case 503:
        return '서비스를 일시적으로 사용할 수 없습니다.';
      default:
        return `서버 오류 (${error.response.status})가 발생했습니다.`;
    }
  }

  // 네트워크 오류 또는 타임아웃
  if (error.message === 'Network Error') return '네트워크 연결을 확인해주세요.';
  if (error.code === 'ECONNABORTED') return '요청 시간이 초과되었습니다.';

  // 기타 오류
  return error.message || '알 수 없는 오류가 발생했습니다.';
}

/**
 * API 에러 타입 가드
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error
  );
}
